import { useState } from 'react';
import { Eye, PencilSimple, Trash } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { DeleteConfirmModal } from './DeleteConfirmModal';

/**
 * Standard inline row actions: View / Edit / Delete.
 *
 * Drop this into any list row (table cell or card footer) to get the three
 * consistent icon buttons used across every list in the app.
 *
 * - Omit a handler to hide that button (e.g. read-only master data has no edit/delete).
 * - `delete` is destructive: by default it pops a confirm dialog. The label and
 *   confirm copy are configurable so entities that map delete to a *safe* action
 *   (matter → archive, hearing → cancel, fee note → credit note, allocation →
 *   withdraw) can describe it correctly while reusing the same control.
 *
 * Usage:
 *   <RowActions
 *     onView={() => navigate(`${basePath}/matters/${m.id}`)}
 *     onEdit={() => setEditing(m)}
 *     onDelete={() => archiveMatter(m.id)}
 *     deleteLabel="Archive"
 *     deleteConfirm={{ title: 'Archive matter?', message: `Archive “${m.name}”? …` }}
 *   />
 */

export interface RowDeleteConfirm {
  title: string;
  message: string;
  /** Button label inside the confirm dialog. Defaults to `deleteLabel`. */
  confirmLabel?: string;
}

export interface RowActionsProps {
  onView?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  /** Tooltip + a11y label for the view button. */
  viewLabel?: string;
  editLabel?: string;
  /** Tooltip + a11y label for the destructive button (e.g. "Archive", "Cancel", "Delete"). */
  deleteLabel?: string;
  /**
   * Icon for the third action. Defaults to a bin. Pass a non-destructive icon when the action is
   * reversible and deletes nothing — e.g. Deactivate / Reactivate an account (CLAUDE.md #25).
   */
  deleteIcon?: React.ReactNode;
  /** 'destructive' (default) turns red on hover; 'neutral' for reversible, non-destructive actions. */
  deleteTone?: 'destructive' | 'neutral';
  /** When set, clicking delete opens a confirm dialog before firing onDelete. */
  deleteConfirm?: RowDeleteConfirm;
  /** Disable individual buttons (e.g. already-archived rows). */
  disableView?: boolean;
  disableEdit?: boolean;
  disableDelete?: boolean;
  /** Prevent row-click handlers firing when an action is clicked. Defaults true. */
  stopPropagation?: boolean;
  className?: string;
}

const BTN =
  'inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground ' +
  'transition-colors hover:bg-accent hover:text-foreground focus:outline-none ' +
  'focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40 disabled:pointer-events-none';

export function RowActions({
  onView,
  onEdit,
  onDelete,
  viewLabel = 'View',
  editLabel = 'Edit',
  deleteLabel = 'Delete',
  deleteIcon,
  deleteTone = 'destructive',
  deleteConfirm,
  disableView,
  disableEdit,
  disableDelete,
  stopPropagation = true,
  className = '',
}: RowActionsProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const guard = (fn?: () => void) => (e: React.MouseEvent) => {
    if (stopPropagation) e.stopPropagation();
    fn?.();
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    if (stopPropagation) e.stopPropagation();
    if (deleteConfirm) setConfirmOpen(true);
    else onDelete?.();
  };

  return (
    <div className={cn('-ml-1.5 inline-flex items-center gap-0.5', className)}>
      {onView && (
        <button
          type="button"
          className={BTN}
          title={viewLabel}
          aria-label={viewLabel}
          disabled={disableView}
          onClick={guard(onView)}
        >
          <Eye className="h-4 w-4" />
        </button>
      )}

      {onEdit && (
        <button
          type="button"
          className={BTN}
          title={editLabel}
          aria-label={editLabel}
          disabled={disableEdit}
          onClick={guard(onEdit)}
        >
          <PencilSimple className="h-4 w-4" />
        </button>
      )}

      {onDelete && (
        <button
          type="button"
          className={deleteTone === 'neutral' ? BTN : `${BTN} hover:bg-red-50 dark:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400`}
          title={deleteLabel}
          aria-label={deleteLabel}
          disabled={disableDelete}
          onClick={handleDeleteClick}
        >
          {deleteIcon ?? <Trash className="h-4 w-4" />}
        </button>
      )}

      {deleteConfirm && (
        <DeleteConfirmModal
          open={confirmOpen}
          title={deleteConfirm.title}
          message={deleteConfirm.message}
          confirmLabel={deleteConfirm.confirmLabel ?? deleteLabel}
          onConfirm={() => {
            setConfirmOpen(false);
            onDelete?.();
          }}
          onCancel={() => setConfirmOpen(false)}
        />
      )}
    </div>
  );
}
